import React, { useState, useCallback, useMemo, useEffect, Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  DocumentIcon,
  PhotoIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";
import { useDropzone } from "react-dropzone";
import { Button } from "components/ui";
import clsx from "clsx";
import { toast } from "sonner";
import { uploadLeadDocuments } from "../../../../../services/leadsService";

// File type configurations
const FILE_TYPES = {
  PDF: {
    extensions: [".pdf"],
    icon: DocumentIcon,
    color: "text-red-600",
    bgColor: "bg-red-50",
  },
  DOCX: {
    extensions: [".docx", ".doc"],
    icon: DocumentIcon,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
  },
  IMAGE: {
    extensions: [".jpg", ".jpeg", ".png"],
    icon: PhotoIcon,
    color: "text-green-600",
    bgColor: "bg-green-50",
  },
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const ACCEPTED_FORMATS = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/msword": [".doc"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};
const fileValidator = (file) => {
  const ext = "." + file.name.split(".").pop().toLowerCase();

  const blockedExtensions = [".jfif"];

  if (blockedExtensions.includes(ext)) {
    return {
      code: "file-invalid-extension",
      message: "JFIF files are not allowed",
    };
  }

  return null;
};




// Utility function to sanitize filenames
const sanitizeFileName = (fileName) => {
  // Remove special characters and replace spaces with underscores
  return fileName
    .replace(/[^a-zA-Z0-9.\-_]/g, "_")
    .replace(/_{2,}/g, "_")
    .toLowerCase();
};

// Utility function to get file type category
const getFileTypeCategory = (fileName) => {
  const extension = "." + fileName.split(".").pop().toLowerCase();

  for (const [category, config] of Object.entries(FILE_TYPES)) {
    if (config.extensions.includes(extension)) {
      return { category, config };
    }
  }
  return { category: "UNKNOWN", config: FILE_TYPES.PDF };
};

// Utility function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export function UploadDocumentModal({
  isOpen,
  onClose,
  onUploadSuccess,
  leadId,
  refreshProgressJourney = () => { }
}) {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Cleanup object URLs when component unmounts or modal closes
  useEffect(() => {
    return () => {
      // Cleanup all object URLs when component unmounts
      files.forEach((fileData) => {
        if (fileData.objectUrl) {
          URL.revokeObjectURL(fileData.objectUrl);
        }
      });
    };
  }, []);

  // Cleanup when modal closes
  useEffect(() => {
    if (!isOpen && files.length > 0) {
      files.forEach((fileData) => {
        if (fileData.objectUrl) {
          URL.revokeObjectURL(fileData.objectUrl);
        }
      });
      setFiles([]);
    }
  }, [isOpen]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach((rejectedFile) => {
        const { file, errors } = rejectedFile;
        errors.forEach((error) => {
          let message = `File "${file.name}" was rejected: `;
          if (error.code === "file-too-large") {
            message += `File size exceeds ${formatFileSize(MAX_FILE_SIZE)} limit`;
          } else if (error.code === "file-invalid-type") {
            message +=
              "Invalid file type. Accepted formats: PDF, DOCX, JPG, PNG";
          } else {
            message += error.message;
          }
          toast.error(message);
        });
      });
    }

    // Process accepted files
    if (acceptedFiles.length > 0) {
      const newFiles = acceptedFiles.map((file) => {
        // Create object URL for image files
        const objectUrl = file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null;

        return {
          id: Math.random().toString(36).substr(2, 9),
          file: file,
          sanitizedName: sanitizeFileName(file.name),
          size: file.size,
          type: file.type,
          status: "pending", // pending, uploading, success, error
          progress: 0,
          error: null,
          objectUrl: objectUrl, // Store object URL for cleanup
        };
      });

      setFiles((prev) => [...prev, ...newFiles]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: ACCEPTED_FORMATS,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    noClick: false,
    noKeyboard: true,
    validator: fileValidator,     // ⬅️ Add this
  });

  const removeFile = (fileId) => {
    setFiles((prev) => {
      const fileToRemove = prev.find((file) => file.id === fileId);
      if (fileToRemove && fileToRemove.objectUrl) {
        URL.revokeObjectURL(fileToRemove.objectUrl);
      }
      return prev.filter((file) => file.id !== fileId);
    });
  };

  const clearAllFiles = () => {
    // Clean up object URLs before clearing files
    files.forEach((fileData) => {
      if (fileData.objectUrl) {
        URL.revokeObjectURL(fileData.objectUrl);
      }
    });
    setFiles([]);
  };

  const validateFiles = () => {
    if (files.length === 0) {
      toast.error("Please select at least one file to upload");
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!validateFiles()) return;

    setIsUploading(true);

    try {
      // Mark all files as uploading
      setFiles((prev) =>
        prev.map((f) => ({ ...f, status: "uploading", progress: 50 }))
      );

      // Get the actual files from the file data
      const actualFiles = files.map((fileData) => fileData.file);

      // Call the API to upload documents
      const response = await uploadLeadDocuments(leadId, actualFiles);

      if (response.success) {
        // Mark all files as successfully uploaded
        setFiles((prev) =>
          prev.map((f) => ({ ...f, status: "success", progress: 100 })),
        );

        // Prepare uploaded files data for callback
        const uploadedFilesData = files.map((fileData) => ({
          name: fileData.sanitizedName,
          originalName: fileData.file.name,
          size: fileData.size,
          type: fileData.type,
          leadId: leadId,
        }));
        refreshProgressJourney();

        // Show success toast
        toast.success(
          `${uploadedFilesData.length} file${uploadedFilesData.length > 1 ? "s" : ""} uploaded successfully!`,
        );

        // Call success callback
        if (onUploadSuccess) {
          onUploadSuccess(uploadedFilesData);
        }

        // Close modal after successful upload
        setTimeout(() => {
          onClose();
          setFiles([]);
          setIsUploading(false);
        }, 1500);
      } else {
        // Handle API error - parse validation errors if present
        let fileErrors = {};

        // console.log('Full response:', response);
        // console.log('Response status:', response.status);
        // console.log('Response data:', response.data);

        if (response.status === 400 && response.data && response.data.data) {
          try {
            // Parse the JSON string in response.data.data
            const errorData = typeof response.data.data === 'string'
              ? JSON.parse(response.data.data)
              : response.data.data;

            // console.log('Parsed error data:', errorData);

            if (errorData.FileResults && errorData.FileResults.length > 0) {
              // Create a map of filename to error message
              errorData.FileResults.forEach(fileResult => {
                if (fileResult.FileName && fileResult.Errors && fileResult.Errors.length > 0) {
                  // Join all errors for this file with line breaks
                  fileErrors[fileResult.FileName] = fileResult.Errors.join(', ');
                  // console.log('Error mapping:', fileResult.FileName, '->', fileErrors[fileResult.FileName]);
                }
              });
              // console.log('All file errors:', fileErrors);
            }
          } catch (parseError) {
            console.error("Error parsing validation data:", parseError);
          }
        }

        // Update files with specific errors
        setFiles((prev) =>
          prev.map((f) => {
            // Match by original file name
            // console.log('Looking for error for file:', f.file.name);
            // console.log('Available error keys:', Object.keys(fileErrors));
            const specificError = fileErrors[f.file.name];
            // console.log('Found error:', specificError);
            
            // Try case-insensitive matching if exact match fails
            let finalError = specificError;
            if (!specificError) {
              const lowerFileName = f.file.name.toLowerCase();
              const matchedKey = Object.keys(fileErrors).find(key => key.toLowerCase() === lowerFileName);
              if (matchedKey) {
                finalError = fileErrors[matchedKey];
                // console.log('Found error with case-insensitive match:', finalError);
              }
            }
            return {
              ...f,
              status: "error",
              progress: 0,
              error: finalError || response.message || "Upload failed"
            };
          }),
        );

        // Show general error toast
        toast.error(response.message || "Upload failed");
        setIsUploading(false);
      }
    } catch (error) {
      console.error("Upload error:", error);

      // Mark all files as error
      setFiles((prev) =>
        prev.map((f) => ({
          ...f,
          status: "error",
          progress: 0,
          error: "Failed to upload"
        }))
      );

      toast.error(error.message || "Failed to upload files. Please try again.");
      setIsUploading(false);
    }
  };


  const renderFilePreview = (fileData) => {
    const { file, sanitizedName, size, status, progress, objectUrl } = fileData;
    const { category, config } = getFileTypeCategory(file.name);
    const Icon = config.icon;
    const isImage = category === "IMAGE";

    return (
      <div
        key={fileData.id}
        className={clsx(
          "group relative flex items-center gap-3 rounded-lg border p-3 transition-all",
          status === "success"
            ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20"
            : status === "error"
              ? "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20"
              : "border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800",
        )}
      >
        {/* File Icon or Image Preview */}
        <div className="relative flex-shrink-0">
          {isImage && objectUrl ? (
            <div className="relative h-12 w-12 overflow-hidden rounded-md">
              <img
                src={objectUrl}
                alt={sanitizedName}
                className="h-full w-full object-cover"
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  e.target.style.display = "none";
                  e.target.parentElement.innerHTML =
                    '<div class="flex h-12 w-12 items-center justify-center rounded-md bg-gray-100 dark:bg-gray-700"><svg class="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>';
                }}
              />
            </div>
          ) : (
            <div
              className={clsx(
                "flex h-12 w-12 items-center justify-center rounded-md",
                config.bgColor,
              )}
            >
              <Icon className={clsx("h-6 w-6", config.color)} />
            </div>
          )}

          {/* Status Icon */}
          {status === "success" && (
            <CheckCircleIcon className="absolute -top-1 -right-1 h-5 w-5 text-green-600" />
          )}
          {/* {status === "error" && (
            <ExclamationTriangleIcon className="absolute -top-1 -right-1 h-5 w-5 text-red-600" />
          )} */}
        </div>

        {/* File Info */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
            {sanitizedName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatFileSize(size)}
          </p>
          {status === "uploading" && (
            <div className="mt-1">
              <div className="h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="bg-primary-600 h-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Uploading... {progress}%
              </p>
            </div>
          )}
          {status === "error" && fileData.error && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {fileData.error}
            </p>
          )}
        </div>

        {/* Remove Button */}
        {!isUploading && status !== "success" && (
          <button
            onClick={() => removeFile(fileData.id)}
            className="opacity-100 cursor-pointer transition-opacity"
            disabled={isUploading}
          >
            <TrashIcon className="h-4 w-4 text-red-400 hover:text-red-600" />
          </button>
        )}
      </div>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[300] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => { }}
        initialFocus={null}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-lg origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 outline-none focus:outline-none">
            {/* Header */}
            <div className="dark:bg-dark-800 flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5">
              <DialogTitle
                as="h3"
                className="dark:text-dark-100 flex items-center gap-2 text-base font-medium text-gray-800"
              >
                <ArrowUpTrayIcon className="h-5 w-5 text-blue-600" />
                Upload Documents
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
                disabled={isUploading}
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            {/* Body (scrollable) */}
            <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
              {/* Dropzone Area */}
              <div
                {...getRootProps()}
                className={clsx(
                  "cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-all",
                  isDragActive
                    ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400 dark:border-gray-600 dark:bg-gray-700/50 dark:hover:border-gray-500",
                  isUploading && "pointer-events-none opacity-75",
                )}
              >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm dark:bg-gray-800">
                    <ArrowUpTrayIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {isDragActive
                        ? "Drop files here"
                        : "Drag & drop files here"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      or click to select files
                    </p>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    <p>Accepted: PDF, DOCX, JPG, PNG</p>
                    <p>Max size: {formatFileSize(MAX_FILE_SIZE)} per file</p>
                  </div>
                </div>
              </div>

              {/* File List */}
              {files.length > 0 && (
                <div className="mt-6">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Selected Files ({files.length})
                    </h3>
                    {!isUploading && (
                      <button
                        onClick={clearAllFiles}
                        className="text-xs cursor-pointer text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {files.map((fileData) => renderFilePreview(fileData))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer (fixed) */}
            <div className="dark:bg-dark-700 flex justify-end gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:px-5 dark:border-gray-700">
              <Button
                onClick={onClose}
                variant="outlined"
                className="min-w-[7rem] rounded-full"
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                color="primary"
                className="min-w-[7rem] rounded-full"
                disabled={isUploading || files.length === 0}
                title={
                  files.length === 0
                    ? "Please select at least one file"
                    : "Upload selected files"
                }
              >
                {isUploading ? (
                  <>
                    <div className="mr-2 size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <ArrowUpTrayIcon className="mr-2 size-4" />
                    Upload {files.length > 0 ? `(${files.length})` : ""}
                  </>
                )}
              </Button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}

export default UploadDocumentModal;
